import { useEffect, useState } from 'react';
import { fetchData } from '../../../apiService';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider, FormHelperText,
  useMediaQuery, useTheme, Chip, CircularProgress, Checkbox,
  InputAdornment, Pagination
} from '@mui/material';
import { Close, Add, Delete, ShoppingCart, Warning, CheckCircle, List, Search } from '@mui/icons-material';
import { formatDate } from '../../../utils/dateUtils';

const SaleOrderModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  SaleOrderDetails,
  setSaleOrderDetails,
  onSave,
  onVerify,
  onConvert,
  customers,
  items,
  loading,
  freightPackingGST,
  companyId,
  branchId,
  token,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Sale Order' : modalAction === 'edit' ? 'Edit Sale Order' : 'View Sale Order';

  // Data loss warning state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [originalSaleOrderDetails, setOriginalSaleOrderDetails] = useState([]);

  // Date validation state
  const [dateError, setDateError] = useState('');
  const [customerError, setCustomerError] = useState('');

  // Verify confirmation modal state
  const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);

  // Get Items modal state
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // Invoice types state
  const [invoiceTypes, setInvoiceTypes] = useState([]);

  // Initialize original data when modal opens
  useEffect(() => {
    if (open && !isViewMode) {
      setOriginalFormData({ ...formData });
      setOriginalSaleOrderDetails([...SaleOrderDetails]);
      setHasUnsavedChanges(false);
    }
    if (open) {
      fetchInvoiceTypes();
    }
  }, [open, modalAction]);

  // Track changes in form data
  useEffect(() => {
    if (!isViewMode && Object.keys(originalFormData).length > 0) {
      const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      const hasDetailChanges = JSON.stringify(SaleOrderDetails) !== JSON.stringify(originalSaleOrderDetails);
      setHasUnsavedChanges(hasFormChanges || hasDetailChanges);
    }
  }, [formData, SaleOrderDetails, originalFormData, originalSaleOrderDetails, isViewMode]);

  // Handle close with unsaved changes check
  const handleCloseClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };

  // Verify handlers
  const handleVerifyClick = () => {
    setVerifyConfirmOpen(true);
  };

  const handleVerifyConfirm = async () => {
    setVerifyConfirmOpen(false);
    if (onVerify) {
      await onVerify(formData.Id, formData.SaleOrderNo);
    }
  };

  const handleVerifyCancel = () => {
    setVerifyConfirmOpen(false);
  };

  // Enhanced save handler that clears unsaved changes flag
  const handleSave = async () => {
    if (!formData.MasterId) {
      setCustomerError('Customer is required');
      return;
    }
    setCustomerError('');
    try {
      await onSave();
      setHasUnsavedChanges(false);
      setOriginalFormData({ ...formData });
      setOriginalSaleOrderDetails([...SaleOrderDetails]);
    } catch (error) {
      // Keep unsaved changes flag if save fails
      console.error('Save failed:', error);
    }
  };
  
  // Fetch Invoice Types
  const fetchInvoiceTypes = async () => {
    if (!companyId || !token) return;
    try {
      const response = await fetchData('InvoiceTypeApi', `/${companyId}`, token);
      setInvoiceTypes(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching invoice types:', error);
      setInvoiceTypes([]);
    }
  };

  // Fetch bill number for a specific invoice type (create mode only)
  const fetchBillNumberForCustomer = async (invoiceTypeId) => {
    if (!invoiceTypeId || modalAction !== 'create') return;
    try {
      const response = await fetchData('SaleOrderApi', `/MaxBillno/${companyId}/${branchId}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      let SaleOrderNo = '';
      if (billPrefix && finYear) SaleOrderNo = `${billPrefix}/${billNumber}/${finYear}`;
      else if (finYear) SaleOrderNo = `${billNumber}/${finYear}`;
      else if (billPrefix) SaleOrderNo = `${billPrefix}/${billNumber}`;
      else SaleOrderNo = `${billNumber}`;
      setFormData(prev => ({ ...prev, SaleOrderNumber: billNumber, SaleOrderNo, SaleOrderPrefix: billPrefix, FinYear: finYear }));
    } catch (error) {
      console.error('Error fetching bill number for invoice type:', error);
    }
  };

  // Helper functions to get customer details
  const getSelectedCustomerGST = () => {
    if (!formData.MasterId) return null;
    const customer = customers.find(c => (c.MasterId || c.Id) === parseInt(formData.MasterId));
    return customer?.GSTNumber || customer?.GSTNo || customer?.GSTIN || null;
  };

  const getSelectedCustomerInvoiceType = () => {
    if (!formData.MasterId) return null;
    const customer = customers.find(c => (c.MasterId || c.Id) === parseInt(formData.MasterId));
    return customer?.InvoiceTypeName || customer?.InvoiceType || null;
  };

  // Helper function to check if IGST should be used based on InvoiceTypeName
  const isIGSTInvoice = () => {
    const invoiceType = (formData.InvoiceTypeName || '').toLowerCase();
    if (invoiceType) return invoiceType.includes('igst');
    // Fallback: check from customers array
    if (!formData.MasterId) return false;
    const customer = customers.find(c => (c.MasterId || c.Id) === parseInt(formData.MasterId));
    return (customer?.InvoiceTypeName || customer?.InvoiceType || '').toLowerCase().includes('igst');
  };

  const customerGSTNo = getSelectedCustomerGST();
  const customerInvoiceType = formData.InvoiceTypeName || getSelectedCustomerInvoiceType();

  const handleChange = (field, value) => {
    // Date validation for SaleOrderDate
    if (field === 'SaleOrderDate') {
      const today = new Date().toISOString().split('T')[0];
      const currentYear = new Date().getFullYear();
      const selectedYear = value ? new Date(value).getFullYear() : null;

      if (selectedYear && selectedYear > currentYear) {
        setDateError('Future year is not allowed. Only current year dates are accepted.');
        return;
      }
      if (selectedYear && selectedYear < currentYear) {
        setDateError('Previous year is not allowed. Only current year dates are accepted.');
        return;
      }
      if (value > today) {
        setDateError('Future date is not allowed');
        return;
      }
      setDateError('');
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If customer changes, resolve InvoiceTypeId/Name and recalculate totals
    if (field === 'MasterId') {
      const selectedCustomer = customers.find(c => (c.MasterId || c.Id) === parseInt(value));
      if (selectedCustomer) {
        const invoiceTypeName = selectedCustomer.InvoiceTypeName || selectedCustomer.InvoiceType || '';
        const gstNumber = selectedCustomer.GSTNumber || selectedCustomer.GSTNo || '';
        // Find InvoiceTypeId from fetched invoiceTypes list
        const invoiceType = invoiceTypes.find(it =>
          it.Name === invoiceTypeName || it.Id === selectedCustomer.InvoiceTypeId
        );
        const invoiceTypeId = invoiceType?.Id || selectedCustomer.InvoiceTypeId || 0;
        setFormData(prev => ({
          ...prev,
          MasterId: value,
          InvoiceTypeId: invoiceTypeId,
          InvoiceTypeName: invoiceTypeName,
          GSTNumber: gstNumber,
        }));
        // Fetch bill number for this invoice type in create mode
        if (invoiceTypeId) fetchBillNumberForCustomer(invoiceTypeId);
      }
      // Recalculate totals with new GST type
      setTimeout(() => {
        calculateTotals(SaleOrderDetails);
      }, 100);
    }
  };

  // Helper function to calculate maximum discount amount
  const getMaxDiscountAmount = (quantity, rate, gstRate) => {
    const qty = parseFloat(quantity) || 0;
    const rt = parseFloat(rate) || 0;
    const gst = parseFloat(gstRate) || 0;

    // For sale orders, we typically use excluding tax calculation
    return qty * rt;
  };

  const handleDetailChange = (index, field, value) => {
    const updatedDetails = [...SaleOrderDetails];

    // Handle discount field validation and bidirectional sync
    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      // Allow empty values but convert them to 0
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: 0 };
        setSaleOrderDetails(updatedDetails);
        calculateTotals(updatedDetails);
        return;
      }

      // Validate numeric input
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;

      // Validate discount percentage not above 100%
      if (field === 'DiscountPercentage') {
        if (numValue > 100) return;
      }

      // Validate discount amount doesn't exceed maximum allowed (100% of item amount)
      if (field === 'DiscountAmount') {
        const currentDetail = updatedDetails[index];
        const quantity = Math.max(0, parseFloat(currentDetail.Quantity) || 0);
        const rate = Math.max(0, parseFloat(currentDetail.Rate) || 0);
        const gstRate = Math.max(0, parseFloat(currentDetail.GSTRate) || 0);
        const maxDiscountAmount = getMaxDiscountAmount(quantity, rate, gstRate);

        if (numValue > maxDiscountAmount) {
          // Block the input if it exceeds 100% of applicable amount
          return;
        }
      }
    }

    updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    
    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const quantity = parseFloat(detail.Quantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const gstRate = parseFloat(detail.GSTRate) || 0;
      const currentUseIGST = isIGSTInvoice(); // Get current GST type

      const amount = quantity * rate;

      let discountAmount = 0, discountPct = 0;
      if (field === 'DiscountAmount') {
        discountAmount = Math.max(0, parseFloat(value) || 0);
        // Ensure discount amount doesn't exceed 100% of amount
        discountAmount = Math.min(discountAmount, amount);
        discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
        // Ensure discount percentage doesn't exceed 100%
        discountPct = Math.min(discountPct, 100);
      } else {
        discountPct = Math.max(0, parseFloat(detail.DiscountPercentage) || 0);
        // Ensure discount percentage doesn't exceed 100%
        discountPct = Math.min(discountPct, 100);
        discountAmount = (amount * discountPct) / 100;
      }

      const taxableAmount = amount - discountAmount;
      const gstAmount = (taxableAmount * gstRate) / 100;

      updatedDetails[index] = {
        ...updatedDetails[index],
        Amount: amount,
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: taxableAmount,
        CGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
        SGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
        IGSTAmount: currentUseIGST ? gstAmount : 0, 
        TotalAmount: taxableAmount + gstAmount
      };
    }
    setSaleOrderDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const handleItemSelect = (index, itemId) => {
    const selectedItem = items.find(i => i.Id === parseInt(itemId));
    if (selectedItem) {
      const updatedDetails = [...SaleOrderDetails];
      const quantity = parseFloat(updatedDetails[index].Quantity) || 0;
      const rate = selectedItem.SaleRate || selectedItem.PurchaseRate || 0;
      const gstRate = selectedItem.GSTRate || 0;
      const discountPct = parseFloat(updatedDetails[index].DiscountPercentage) || 0;
      const currentUseIGST = isIGSTInvoice(); // Get current GST type

      const amount = quantity * rate;
      const discountAmount = (amount * discountPct) / 100;
      const taxableAmount = amount - discountAmount;
      const gstAmount = (taxableAmount * gstRate) / 100;

      updatedDetails[index] = {
        ...updatedDetails[index], 
        ItemId: itemId, 
        ItemName: selectedItem.ItemName || selectedItem.Name || '',
        Code: selectedItem.Code || '',
        HSNCode: selectedItem.HSNCode || '', 
        GSTRate: gstRate, 
        Rate: rate,
        Amount: amount, 
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100, 
        TaxableAmount: taxableAmount,
        CGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
        SGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
        IGSTAmount: currentUseIGST ? gstAmount : 0, 
        TotalAmount: taxableAmount + gstAmount
      };
      setSaleOrderDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  };

  const addDetailRow = () => {
    setSaleOrderDetails([...SaleOrderDetails, {
      Id: 0, ItemId: '', HSNCode: '', GSTRate: 0, Quantity: 0, Rate: 0, Amount: 0,
      DiscountPercentage: 0, DiscountAmount: 0, TaxableAmount: 0, 
      CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0, IsActive: true
    }]);
  };

  const removeDetailRow = (index) => {
    const updatedDetails = SaleOrderDetails.filter((_, i) => i !== index);
    setSaleOrderDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const calculateTotals = (details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    // Get default GST rates from API
    const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate || freightPackingGST?.freight?.GstRate) || 0;
    const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate || freightPackingGST?.packing?.GstRate) || 0;
    
    // Find the highest GST rate from items
    const maxItemGSTRate = details.length > 0 
      ? Math.max(...details.map(d => parseFloat(d.GSTRate) || 0))
      : 0;
    
    // Use higher of default or max item GST rate (never downgrade from default)
    const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
    const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

    const freightAmount = parseFloat(formData.FreightAmount) || 0;
    const packingAmount = parseFloat(formData.PackingAmount) || 0;
    
    // Calculate freight and packing GST as single amounts (like Purchase Order system)
    const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
    const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;
    
    // totals already contains line item GST amounts - don't modify them
    // Freight and packing GST are added separately to grand total
    const grandTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount;

    setFormData(prev => ({
      ...prev, 
      ...totals, 
      FreightGSTRate: effectiveFreightGSTRate,
      FreightGSTAmount: freightGSTAmount, 
      PackingGSTRate: effectivePackingGSTRate,
      PackingGSTAmount: packingGSTAmount,
      RoundOff: parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2)), 
      TotalAmount: Math.round(grandTotal)
    }));
  };

  // Recalculate when freight/packing amounts or GST rates change
  useEffect(() => {
    calculateTotals(SaleOrderDetails);
  }, [formData.FreightAmount, formData.FreightGSTRate, formData.PackingAmount, formData.PackingGSTRate]);

  // Recalculate all items when Customer changes (affects GST type)
  useEffect(() => {
    if (formData.MasterId && SaleOrderDetails.length > 0) {
      // Recalculate all line items with new GST type
      const currentUseIGST = isIGSTInvoice();
      const updatedDetails = SaleOrderDetails.map(detail => {
        const gstRate = parseFloat(detail.GSTRate) || 0;
        const taxableAmount = parseFloat(detail.TaxableAmount) || 0;
        const gstAmount = (taxableAmount * gstRate) / 100;
        
        return {
          ...detail,
          CGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
          SGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
          IGSTAmount: currentUseIGST ? gstAmount : 0, 
          TotalAmount: taxableAmount + gstAmount
        };
      });
      
      setSaleOrderDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  }, [formData.MasterId]);

  // Get Items Modal Functions
  const handleOpenGetItemsModal = async () => {
    setGetItemsModalOpen(true);
    setGetItemsLoading(true);
    
    try {
      // Simulate loading time for items
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setGetItemsLoading(false);
    }
  };

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);
  };

  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      setSelectedItems(prev => [...prev, {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.SaleRate || item.Rate || 0,
        selectedDiscountPercentage: 0,
        selectedDiscountAmount: 0
      }]);
    } else {
      setSelectedItems(prev => prev.filter(selected => selected.Id !== item.Id));
    }
  };

  const handleSelectedItemChange = (itemId, field, value) => {
    setSelectedItems(prev => prev.map(item => {
      if (item.Id !== itemId) return item;

      const updatedItem = { ...item, [field]: value };

      // Bidirectional discount sync with validation
      if (field === 'selectedDiscountPercentage') {
        const discountPct = Math.min(100, Math.max(0, parseFloat(value) || 0));
        const quantity = parseFloat(updatedItem.selectedQuantity) || 0;
        const rate = parseFloat(updatedItem.selectedRate || item.SaleRate || item.Rate) || 0;
        const maxDiscountAmount = getMaxDiscountAmount(quantity, rate, updatedItem.GSTRate);
        const discountAmount = (maxDiscountAmount * discountPct) / 100;
        
        updatedItem.selectedDiscountPercentage = discountPct;
        updatedItem.selectedDiscountAmount = discountAmount;
      } else if (field === 'selectedDiscountAmount') {
        const quantity = parseFloat(updatedItem.selectedQuantity) || 0;
        const rate = parseFloat(updatedItem.selectedRate || item.SaleRate || item.Rate) || 0;
        const maxDiscountAmount = getMaxDiscountAmount(quantity, rate, updatedItem.GSTRate);
        const discountAmount = Math.min(maxDiscountAmount, Math.max(0, parseFloat(value) || 0));
        const discountPct = maxDiscountAmount > 0 ? (discountAmount / maxDiscountAmount) * 100 : 0;
        
        updatedItem.selectedDiscountAmount = discountAmount;
        updatedItem.selectedDiscountPercentage = discountPct;
      }

      return updatedItem;
    }));
  };

  const handleGetItemsSearch = (event) => {
    setGetItemsSearchValue(event.target.value);
    setCurrentItemsPage(1);
  };

  const getFilteredItems = () => {
    if (!items) return [];
    
    return items.filter(item => {
      const searchTerm = getItemsSearchValue.toLowerCase();
      return (
        (item.ItemName || '').toLowerCase().includes(searchTerm) ||
        (item.HSNCode || item.HSN || '').toLowerCase().includes(searchTerm) ||
        (item.Code || '').toLowerCase().includes(searchTerm)
      );
    });
  };

  const getPaginatedItems = () => {
    const filtered = getFilteredItems();
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  };

  const isItemAlreadyInSaleOrder = (itemId) => {
    return SaleOrderDetails.some(detail => detail.ItemId === itemId);
  };

  // Add selected items to sale order details
  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    setAddingItemsLoading(true);
    
    try {
      const itemsToAdd = selectedItems.filter(item => 
        !isItemAlreadyInSaleOrder(item.Id) && 
        parseFloat(item.selectedQuantity) > 0
      );

      if (itemsToAdd.length === 0) {
        handleCloseGetItemsModal();
        return;
      }

      const currentUseIGST = isIGSTInvoice();

      const newDetails = itemsToAdd.map(item => {
        const quantity = parseFloat(item.selectedQuantity) || 0;
        const rate = parseFloat(item.selectedRate || item.SaleRate || item.Rate) || 0;
        const gstRate = parseFloat(item.GSTRate) || 0;
        const discountPct = parseFloat(item.selectedDiscountPercentage) || 0;
        const discountAmount = parseFloat(item.selectedDiscountAmount) || 0;
        
        const amount = quantity * rate;
        const finalDiscountAmount = discountAmount > 0 ? discountAmount : (amount * discountPct) / 100;
        const taxableAmount = amount - finalDiscountAmount;
        const gstAmount = (taxableAmount * gstRate) / 100;

        return {
          Id: 0,
          ItemId: item.Id,
          ItemName: item.ItemName || item.Name,
          HSNCode: item.HSNCode || item.HSN || '',
          Quantity: quantity,
          Rate: rate,
          Amount: amount,
          DiscountPercentage: finalDiscountAmount > 0 ? (finalDiscountAmount / amount) * 100 : discountPct,
          DiscountAmount: finalDiscountAmount,
          GSTRate: gstRate,
          TaxableAmount: taxableAmount,
          CGSTAmount: currentUseIGST ? 0 : gstAmount / 2,
          SGSTAmount: currentUseIGST ? 0 : gstAmount / 2,
          IGSTAmount: currentUseIGST ? gstAmount : 0,
          TotalAmount: taxableAmount + gstAmount,
          IsActive: true
        };
      });

      const updatedDetails = [...SaleOrderDetails, ...newDetails];
      setSaleOrderDetails(updatedDetails);

      handleCloseGetItemsModal();

      // Calculate totals
      calculateTotals(updatedDetails);
      setHasUnsavedChanges(true);

    } catch (error) {
      console.error('Error adding selected items:', error);
    } finally {
      setAddingItemsLoading(false);
    }
  };

  const formatCurrency = (amt) => `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const disabledFieldStyle = {
    '& .MuiInputBase-input.Mui-disabled': {
      color: '#5D4037',
      fontWeight: 700,
      WebkitTextFillColor: '#5D4037'
    }
  };
  
  const compactInput = { 
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' }, 
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    ...(isViewMode && disabledFieldStyle)
  };
  
  const compactInputRight = { 
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right', fontWeight: 700 }, 
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    ...(isViewMode && disabledFieldStyle)
  };

  const tinyInput = { 
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.7rem', textAlign: 'right', fontWeight: 700 }, 
    minWidth: 0,
    ...(isViewMode && disabledFieldStyle)
  };

  const tinyInputCenter = { 
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.7rem', textAlign: 'center' }, 
    minWidth: 0,
    ...(isViewMode && disabledFieldStyle)
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key if there are unsaved changes
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            if (hasUnsavedChanges && !isViewMode) {
              setCancelConfirmOpen(true);
              return;
            }
          }
          onClose();
        }} 
        maxWidth="xl" 
        fullWidth 
        fullScreen={isMobile}
        disableEscapeKeyDown={hasUnsavedChanges && !isViewMode}
        slotProps={{
          backdrop: {
            onClick: (e) => {
              if (hasUnsavedChanges && !isViewMode) {
                e.stopPropagation();
                setCancelConfirmOpen(true);
              }
            }
          }
        }}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2 } }}
      >
        
        <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#1e40af', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
              {hasUnsavedChanges && !isViewMode && (
                <Chip 
                  label="Unsaved Changes"
                  size="small"
                  sx={{ 
                    fontSize: '0.65rem', 
                    height: '20px', 
                    bgcolor: '#fef3c7', 
                    color: '#92400e',
                    border: '1px solid #f59e0b',
                    ml: 1
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleCloseClick} size="small" sx={{ p: 0.5 }}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1.5 : 2, bgcolor: '#fff' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Sale Order Information</Typography>
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" label="Sale Order No" value={formData.SaleOrderNo || ''} 
                disabled={true} sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: '#f0f9ff' } }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="Sale Order Date"
                  value={formatDate(formData.SaleOrderDate) || '-'} disabled
                  InputLabelProps={{ shrink: true }} sx={compactInput} />
              ) : (
                <TextField fullWidth size="small" type="date" label="Sale Order Date"
                  value={formData.SaleOrderDate || ''}
                  onChange={(e) => handleChange('SaleOrderDate', e.target.value)} disabled={isViewMode}
                  inputProps={{ min: `${new Date().getFullYear()}-01-01`, max: new Date().toISOString().split('T')[0] }}
                  error={!!dateError}
                  helperText={dateError || ''}
                  InputLabelProps={{ shrink: true }} sx={compactInput} />
              )}
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small" sx={compactInput} error={!isViewMode && (!formData.MasterId || !!customerError)}>
                <InputLabel>Customer *</InputLabel>
                <Select value={formData.MasterId || ''} onChange={(e) => { handleChange('MasterId', e.target.value); setCustomerError(''); }} 
                  disabled={isViewMode} label="Customer *" sx={{ fontSize: '0.8rem' }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}>
                  {customers.map(c => <MenuItem key={c.MasterId || c.Id} value={c.MasterId || c.Id} sx={{ fontSize: '0.75rem', py: 0.5 }}>{c.Name}</MenuItem>)}
                </Select>
                {customerError && <FormHelperText>{customerError}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" label="GST No" value={customerGSTNo || '-'} disabled 
                sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: customerGSTNo ? '#f0fdf4' : '#f9fafb' } }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" label="Invoice Type" value={customerInvoiceType || '-'} disabled 
                sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: customerInvoiceType ? '#fef3c7' : '#f9fafb' } }} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af' }}>Sale Order Items</Typography>
            {!isViewMode && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" startIcon={<List sx={{ fontSize: 16 }} />} onClick={handleOpenGetItemsModal} 
                  variant="contained" sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}>
                  Get Items
                </Button>
                {/* <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={addDetailRow} 
                  variant="outlined" sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0 }}>Add</Button> */}
              </Box>
            )}
          </Box>

          {isMobile ? (
          <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 1.5 }}>
            {SaleOrderDetails.map((detail, index) => (
              <Box key={index} sx={{ 
                border: '1px solid #d1d5db', 
                borderRadius: 2, 
                p: 1.5, 
                mb: 1.5, 
                bgcolor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
                {/* Header with Item number and Delete */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      bgcolor: '#1e40af', 
                      color: '#fff', 
                      borderRadius: '50%', 
                      width: 22, 
                      height: 22, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}>
                      {index + 1}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>
                      {(() => { const it = items.find(i => i.Id === parseInt(detail.ItemId)); if (it) { return it.Code ? `${it.Code} - ${it.ItemName || it.Name}` : (it.ItemName || it.Name); } const fallbackName = detail.ItemName || detail.Name; return fallbackName ? (detail.Code ? `${detail.Code} - ${fallbackName}` : fallbackName) : 'Select Item'; })()}
                    </Typography>
                  </Box>
                  {!isViewMode && (
                    <IconButton size="small" onClick={() => removeDetailRow(index)} 
                      sx={{ p: 0.5, bgcolor: '#fef2f2', color: '#dc2626', '&:hover': { bgcolor: '#fee2e2' } }}>
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>

                {/* Item Name (read-only text) */}
                <Box sx={{ mb: 1, px: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>Item</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#1e293b' }}>
                    {(() => { const it = items.find(i => i.Id === parseInt(detail.ItemId)); if (it) { return it.Code ? `${it.Code} - ${it.ItemName || it.Name}` : (it.ItemName || it.Name); } const fallbackName = detail.ItemName || detail.Name; return fallbackName ? (detail.Code ? `${detail.Code} - ${fallbackName}` : fallbackName) : '-'; })()}
                  </Typography>
                </Box>

                {/* Row 1: HSN, GST%, Qty */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>HSN Code</Typography>
                    <TextField 
                      size="small" 
                      value={detail.HSNCode || ''} 
                      onChange={(e) => handleDetailChange(index, 'HSNCode', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      placeholder="-"
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' }, ...(isViewMode && disabledFieldStyle) }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>GST %</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={detail.GSTRate || ''} 
                      onChange={(e) => handleDetailChange(index, 'GSTRate', e.target.value)} 
                      disabled
                      fullWidth 
                      placeholder="0"
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' }, ...disabledFieldStyle }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>Quantity</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={detail.Quantity || ''} 
                      onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      placeholder="0"
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center', fontWeight: 500 }, ...(isViewMode && disabledFieldStyle) }} 
                    />
                  </Box>
                </Box>

                {/* Row 2: Rate, Disc%, Disc Amt */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>Rate (₹)</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={detail.Rate || ''} 
                      onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      placeholder="0.00"
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right', fontWeight: 700 }, ...(isViewMode && disabledFieldStyle) }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>Disc %</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={detail.DiscountPercentage || 0} 
                      onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      placeholder="0"
                      inputProps={{ max: 100, min: 0, step: 0.01 }}
                      title={`Discount percentage (max 100%)`}
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right', fontWeight: 700 }, ...(isViewMode && disabledFieldStyle) }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>Disc Amt</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={detail.DiscountAmount || 0} 
                      onChange={(e) => handleDetailChange(index, 'DiscountAmount', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      placeholder="0.00"
                      inputProps={{ min: 0, step: 0.01 }}
                      title={`Discount amount (max ₹${getMaxDiscountAmount(detail.Quantity, detail.Rate, detail.GSTRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of item amount)`}
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right', fontWeight: 700 }, ...(isViewMode && disabledFieldStyle) }} 
                    />
                  </Box>
                </Box>

                {/* Row 3: Total */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>Total</Typography>
                    <Box sx={{ 
                      bgcolor: '#dbeafe', 
                      borderRadius: 1, 
                      py: 0.75, 
                      px: 0.5,
                      textAlign: 'center',
                      border: '1px solid #93c5fd'
                    }}>
                      <Typography sx={{ fontSize: '0.8rem', color: '#1e40af', fontWeight: 800 }}>
                        {formatCurrency(detail.TotalAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
            {SaleOrderDetails.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: '#6b7280', bgcolor: '#f9fafb', borderRadius: 2, border: '1px dashed #d1d5db' }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>No items added</Typography>
                <Typography variant="caption">Tap "+ Add" button to add items</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, mb: 1.5, overflow: 'hidden' }}>
            <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)' }}>
                    <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '25%' }}>Item</th>
                    <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '6%' }}>HSN</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>GST%</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>Qty</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '7%' }}>Rate</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>Disc%</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '7%' }}>Disc Amt</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '8%' }}>Taxable</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '7%' }}>GST Amt</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '8%' }}>Total</th>
                    {!isViewMode && <th style={{ padding: '8px 4px', width: '5%', background: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)' }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {SaleOrderDetails.map((detail, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f1f5f9', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <td style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#1e293b', fontWeight: 500 }}>
                        {(() => { const it = items.find(i => i.Id === parseInt(detail.ItemId)); if (it) { return it.Code ? `${it.Code} - ${it.ItemName || it.Name}` : (it.ItemName || it.Name); } const fallbackName = detail.ItemName || detail.Name; return fallbackName ? (detail.Code ? `${detail.Code} - ${fallbackName}` : fallbackName) : '-'; })()}
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" value={detail.HSNCode || ''} onChange={(e) => handleDetailChange(index, 'HSNCode', e.target.value)} 
                          disabled={isViewMode} sx={{ ...tinyInputCenter, width: '100%' }} />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" type="number" value={detail.GSTRate || ''} onChange={(e) => handleDetailChange(index, 'GSTRate', e.target.value)} 
                          disabled sx={{ ...tinyInputCenter, width: '100%', ...disabledFieldStyle }} />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" type="number" value={detail.Quantity || ''} onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} 
                          disabled={isViewMode} sx={{ ...tinyInputCenter, width: '100%' }} />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" type="number" value={detail.Rate || ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} 
                          disabled={isViewMode} sx={{ ...tinyInput, width: '100%' }} />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" type="number" value={detail.DiscountPercentage || 0} onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)} 
                          disabled={isViewMode} sx={{ ...tinyInputCenter, width: '100%' }} 
                          inputProps={{ max: 100, min: 0, step: 0.01 }}
                          title={`Discount percentage (max 100%)`} />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" type="number" value={detail.DiscountAmount || 0} onChange={(e) => handleDetailChange(index, 'DiscountAmount', e.target.value)} 
                          disabled={isViewMode} sx={{ ...tinyInput, width: '100%' }} 
                          inputProps={{ min: 0, step: 0.01 }}
                          title={`Discount amount (max ₹${getMaxDiscountAmount(detail.Quantity, detail.Rate, detail.GSTRate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of item amount)`} />
                      </td>
                      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(detail.TaxableAmount)}</td>
                      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 700, color: '#059669' }}>
                        {isIGSTInvoice() ? formatCurrency(detail.IGSTAmount) : formatCurrency((detail.CGSTAmount || 0) + (detail.SGSTAmount || 0))}
                      </td>
                      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(detail.TotalAmount)}</td>
                      {!isViewMode && (
                        <td style={{ padding: '4px' }}>
                          <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.25, color: '#ef4444' }}>
                            <Delete sx={{ fontSize: 14 }} />
                          </IconButton>
                        </td>
                      )}
                    </tr>
                  ))}
                  {SaleOrderDetails.length === 0 && (
                    <tr><td colSpan={isViewMode ? 10 : 11} style={{ textAlign: 'center', padding: '16px', color: '#6b7280' }}>No items added</td></tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        )}

          <Divider sx={{ my: 1 }} />

          <Grid container spacing={isMobile ? 1 : 2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block' }}>Additional Information</Typography>
              <Grid container spacing={0.75}>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="number" label="Freight" value={formData.FreightAmount || ''} 
                    onChange={(e) => handleChange('FreightAmount', e.target.value)} disabled={isViewMode} sx={compactInputRight} />
                </Grid>              
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="number" label="Packing" value={formData.PackingAmount || ''} 
                    onChange={(e) => handleChange('PackingAmount', e.target.value)} disabled={isViewMode} sx={compactInputRight} />
                </Grid>              
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''} 
                    onChange={(e) => handleChange('Remarks', e.target.value)} disabled={isViewMode} 
                    inputProps={{ maxLength: 100 }}
                    multiline rows={1}
                    sx={compactInput} />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block' }}>Sale Order Summary</Typography>
              <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 1 }}>
                {[
                  { label: 'Sub Total', value: formData.Amount },
                  { label: 'Discount', value: formData.DiscountAmount, isNeg: parseFloat(formData.DiscountAmount) > 0 },
                  { label: 'Taxable', value: formData.TaxableAmount },
                  ...(isIGSTInvoice() ? [
                    { label: 'IGST', value: formData.IGSTAmount }
                  ] : [
                    { label: 'CGST', value: formData.CGSTAmount },
                    { label: 'SGST', value: formData.SGSTAmount }
                  ]),
                  ...((parseFloat(formData.FreightAmount) || 0) > 0 ? [
                    { label: 'Freight', value: parseFloat(formData.FreightAmount) || 0 },
                    { label: `Freight GST (${formData.FreightGSTRate || 0}%)`, value: parseFloat(formData.FreightGSTAmount) || 0 },
                  ] : []),
                  ...((parseFloat(formData.PackingAmount) || 0) > 0 ? [
                    { label: 'Packing', value: parseFloat(formData.PackingAmount) || 0 },
                    { label: `Packing GST (${formData.PackingGSTRate || 0}%)`, value: parseFloat(formData.PackingGSTAmount) || 0 },
                  ] : []),
                  { label: 'Round Off', value: formData.RoundOff },
                ].map((row, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">{row.label}:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: row.isNeg ? '#dc2626' : 'inherit' }}>
                      {row.isNeg ? '- ' : ''}{formatCurrency(row.value)}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#1e40af' }}>Grand Total:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#1e40af', fontSize: '0.9rem' }}>{formatCurrency(formData.TotalAmount)}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', gap: 1 }}>
          <Button 
            onClick={handleCloseClick} 
            variant="outlined" 
            size="small" 
            sx={{ 
              borderColor: '#d1d5db', 
              color: '#374151', 
              fontSize: '0.8rem',
              px: 2,
              py: 0.75,
              textTransform: 'none',
              '&:hover': { borderColor: '#9ca3af', bgcolor: '#f3f4f6' }
            }}
          >
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
          
          {/* Show Verify button in VIEW mode OR in EDIT mode with no changes, when order is not yet verified */}
          {formData.Id && !formData.IsVerified && modalAction === 'view' && (
            <Button 
              onClick={handleVerifyClick} 
              variant="contained" 
              size="small" 
              startIcon={<CheckCircle />}
              sx={{ 
                background: 'linear-gradient(135deg, #16a34a, #15803d)', 
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #15803d, #166534)',
                  boxShadow: '0 4px 8px rgba(22, 163, 74, 0.4)'
                }
              }}
            >
              Verify Sale Order
            </Button>
          )}

          {/* Show Request (Convert to Sale) button in VIEW mode when verified and not yet converted */}
          {formData.Id && formData.IsVerified && !formData.IsConverted && modalAction === 'view' && onConvert && (
            <Button
              onClick={() => onConvert(formData.Id, formData.SaleOrderNo)}
              variant="contained"
              size="small"
              startIcon={<ShoppingCart />}
              sx={{
                background: 'linear-gradient(135deg, #e67e22, #d35400)',
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(230, 126, 34, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d35400, #b94600)',
                  boxShadow: '0 4px 8px rgba(230, 126, 34, 0.4)'
                }
              }}
            >
              Request
            </Button>
          )}

          {/* Show Update button when creating OR when editing with changes */}
          {!isViewMode && (modalAction === 'create' || hasUnsavedChanges) && (
            <Button 
              onClick={handleSave} 
              variant="contained" 
              size="small" 
              disabled={loading || !formData.MasterId || SaleOrderDetails.length === 0}
              sx={{ 
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', 
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #2563eb, #1e40af)',
                  boxShadow: '0 4px 8px rgba(59, 130, 246, 0.4)'
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              {modalAction === 'create' ? 'Create Sale Order' : 'Update Sale Order'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <Dialog
          open={cancelConfirmOpen}
          onClose={handleStayOnPage}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                backgroundColor: '#fef3c7', 
                borderRadius: '50%', 
                width: 40, 
                height: 40, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Unsaved Changes
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
              You have unsaved changes to the Sale order that will be lost if you leave this page.
            </Typography>
            <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
              Are you sure you want to exit without saving your changes?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={handleStayOnPage}
              variant="outlined"
              sx={{ 
                borderColor: '#1e40af', 
                color: '#1e40af', 
                '&:hover': { borderColor: '#1d4ed8', backgroundColor: '#dbeafe' } 
              }}
            >
              Stay on Page
            </Button>
            <Button
              onClick={handleConfirmCancel}
              variant="contained"
              sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
            >
              Exit Without Saving
            </Button>
          </DialogActions>
        </Dialog>

      {/* Verify Confirmation Modal */}
      <Dialog
        open={verifyConfirmOpen}
        onClose={handleVerifyCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              backgroundColor: '#dcfce7', 
              borderRadius: '50%', 
              width: 40, 
              height: 40, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <CheckCircle sx={{ color: '#16a34a', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Verify Sale Order
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to verify Sale order <strong style={{ color: '#1e293b' }}>{formData.SaleOrderNo}</strong>?
          </Typography>
          <Typography sx={{ color: '#16a34a', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            Once verified, this Sale order will be confirmed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleVerifyCancel}
            variant="outlined"
            sx={{ 
              borderColor: '#e2e8f0', 
              color: '#64748b', 
              '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } 
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyConfirm}
            variant="contained"
            sx={{ 
              backgroundColor: '#16a34a', 
              '&:hover': { backgroundColor: '#15803d' } 
            }}
          >
            Verify Sale Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '8px',
            m: isMobile ? 0 : 1,
            maxHeight: '80vh',
            width: isMobile ? '100%' : '85%',
            maxWidth: '1100px',
            minWidth: '850px'
          }
        }}
      >
        <DialogTitle sx={{
          py: 0.75,
          px: 1.5,
          borderBottom: '1px solid #d1d5db',
          bgcolor: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: '#fff',
          minHeight: '40px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <List sx={{ color: '#fff', fontSize: 16 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', fontSize: '0.85rem' }}>
                Get Items ({getFilteredItems().length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small" sx={{ color: '#fff', p: 0.25 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280', fontSize: '0.85rem' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name, HSN code, or item code..."
                  value={getItemsSearchValue}
                  onChange={handleGetItemsSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 16, color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                    endAdornment: getItemsSearchValue && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setGetItemsSearchValue('')}
                          sx={{ p: 0.25 }}
                        >
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' },
                    '& .MuiOutlinedInput-root': { bgcolor: '#fff' }
                  }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 1.5, p: 1, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #bfdbfe' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 8).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Code ? `${item.Code} - ` : ''}${item.ItemName || item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    ))}
                    {selectedItems.length > 8 && (
                      <Chip label={`+${selectedItems.length - 8} more`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    )}
                  </Box>
                </Box>
              )}

              {/* Items Table */}
              <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', height: '32px' }}>
                        <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '4%', fontSize: '0.65rem' }}>
                          <Checkbox
                            size="small"
                            checked={selectedItems.length === getPaginatedItems().length && getPaginatedItems().length > 0}
                            indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems().length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                getPaginatedItems().forEach(item => {
                                  if (!selectedItems.find(si => si.Id === item.Id)) {
                                    handleItemSelection(item, true);
                                  }
                                });
                              } else {
                                getPaginatedItems().forEach(item => {
                                  handleItemSelection(item, false);
                                });
                              }
                            }}
                            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' }, '& .MuiSvgIcon-root': { fontSize: 14 } }}
                          />
                        </th>
                        <th style={{ padding: '4px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '28%', fontSize: '0.65rem' }}>Item</th>
                        <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem' }}>HSN</th>
                        <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%', fontSize: '0.65rem' }}>GST%</th>
                        <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '7%', fontSize: '0.65rem' }}>Qty</th>
                        <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>Rate</th>
                        <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%', fontSize: '0.65rem' }}>Disc%</th>
                        <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '9%', fontSize: '0.65rem' }}>Disc Amt</th>
                        <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedItems().map((item, index) => {
                        const selectedItem = selectedItems.find(si => si.Id === item.Id);
                        const isSelected = !!selectedItem;
                        const isAlreadyAdded = isItemAlreadyInSaleOrder(item.Id);

                        return (
                          <tr key={item.Id} style={{
                            borderBottom: '1px solid #e5e7eb',
                            background: isSelected ? '#eff6ff' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                            opacity: isAlreadyAdded ? 0.5 : 1
                          }}>
                            <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isAlreadyAdded}
                                onChange={(e) => handleItemSelection(item, e.target.checked)}
                                sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }}
                              />
                            </td>
                            <td style={{ padding: '3px 8px', fontWeight: 500, fontSize: '0.75rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span>{item.Code ? `${item.Code} - ${item.ItemName || item.Name}` : (item.ItemName || item.Name)}</span>
                                {isAlreadyAdded && (
                                  <Chip label="Added" size="small" color="success" sx={{ fontSize: '0.6rem', height: 14 }} />
                                )}
                              </Box>
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.7rem', color: '#6b7280' }}>{item.HSNCode || item.HSN || '-'}</td>
                            <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#1e40af' }}>{item.GSTRate || 0}%</td>
                            <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedQuantity}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                  inputProps={{ min: 1 }}
                                  sx={{
                                    width: 50,
                                    '& .MuiInputBase-input': {
                                      py: 0.2,
                                      px: 0.3,
                                      fontSize: '0.7rem',
                                      textAlign: 'center',
                                      height: '16px'
                                    },
                                    '& .MuiOutlinedInput-root': { height: '24px' }
                                  }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedRate || item.SaleRate || item.Rate || 0}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                  inputProps={{ min: 0 }}
                                  sx={{
                                    width: 65,
                                    '& .MuiInputBase-input': {
                                      py: 0.2,
                                      px: 0.3,
                                      fontSize: '0.7rem',
                                      textAlign: 'right',
                                      height: '16px'
                                    },
                                    '& .MuiOutlinedInput-root': { height: '24px' }
                                  }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>
                                  ₹{(item.SaleRate || item.Rate || 0).toFixed(2)}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountPercentage}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountPercentage', e.target.value)}
                                  disabled={!selectedItem.selectedQuantity || parseFloat(selectedItem.selectedQuantity) <= 0}
                                  inputProps={{ min: 0, max: 100 }}
                                  sx={{
                                    width: 45,
                                    '& .MuiInputBase-input': {
                                      py: 0.2,
                                      px: 0.3,
                                      fontSize: '0.7rem',
                                      textAlign: 'center',
                                      height: '16px'
                                    },
                                    '& .MuiOutlinedInput-root': { height: '24px' }
                                  }}
                                  title={`Discount percentage (max 100%)`}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>0</span>
                              )}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedDiscountAmount}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountAmount', e.target.value)}
                                  disabled={!selectedItem.selectedQuantity || parseFloat(selectedItem.selectedQuantity) <= 0}
                                  inputProps={{ min: 0 }}
                                  sx={{
                                    width: 60,
                                    '& .MuiInputBase-input': {
                                      py: 0.2,
                                      px: 0.3,
                                      fontSize: '0.7rem',
                                      textAlign: 'right',
                                      height: '16px'
                                    },
                                    '& .MuiOutlinedInput-root': { height: '24px' }
                                  }}
                                  title={`Discount amount (max ₹${((selectedItem.selectedQuantity || 0) * (selectedItem.selectedRate || item.SaleRate || item.Rate || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - 100% of item amount)`}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                              {isSelected ? (
                                (() => {
                                  const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                  const rate = parseFloat(selectedItem.selectedRate || item.SaleRate || item.Rate) || 0;
                                  const discPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
                                  const discAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
                                  const amount = qty * rate;
                                  const finalDiscAmt = discAmt > 0 ? discAmt : (amount * discPct) / 100;
                                  const taxable = amount - finalDiscAmt;
                                  const gstAmt = (taxable * (item.GSTRate || 0)) / 100;
                                  return `₹${(taxable + gstAmt).toFixed(2)}`;
                                })()
                              ) : (
                                `₹${(item.SaleRate || item.Rate || 0).toFixed(2)}`
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {getPaginatedItems().length === 0 && (
                        <tr>
                          <td colSpan="9" style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>
                            {getItemsSearchValue ? 'No items found matching your search' : 'No items available'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Pagination */}
              {Math.ceil(getFilteredItems().length / itemsPerPage) > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(getFilteredItems().length / itemsPerPage)}
                    page={currentItemsPage}
                    onChange={(e, page) => setCurrentItemsPage(page)}
                    size="small"
                    sx={{ '& .MuiPaginationItem-root': { fontSize: '0.75rem' } }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1, borderTop: '1px solid #d1d5db', bgcolor: '#f8fafc' }}>
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', mr: 'auto' }}>
            {selectedItems.length > 0 && `${selectedItems.length} item(s) selected`}
          </Typography>
          <Button onClick={handleCloseGetItemsModal} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedItems}
            variant="contained"
            size="small"
            disabled={selectedItems.length === 0 || addingItemsLoading}
            sx={{ bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}
          >
            {addingItemsLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Add {selectedItems.length > 0 ? `${selectedItems.length} ` : ''}Items
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SaleOrderModal;
